import type { TimelineEntry, TimelineEvent, TimelineMessage } from '@aurora/contracts';
import { isTimelineMessage } from '@aurora/contracts';
import {
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  Clock,
  GitMerge,
  Link2,
  ListChecks,
  Lock,
  Mail,
  Sparkles,
  Tag as TagIcon,
  UserRound,
  Users,
  Zap,
} from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '../../lib/cn';
import { absoluteTime, relativeTime, splitLinks } from '../../lib/format';
import { Avatar, Badge, Card } from '../../components/ui';
import { MessageAttachments, type StoredAttachment } from './attachments';
import { levelLabel } from './priority-matrix';

/**
 * The conversation: messages and what happened between them, in order.
 *
 * Messages are text, not HTML. Bodies arrive from email and from the portal,
 * so rendering them as markup would be a stored cross-site scripting hole;
 * web addresses are made clickable by splitting the text, never by parsing it.
 * Every message lays itself out from its own first strong character, so an
 * Arabic question and an English answer sit in one conversation correctly.
 */

/** Names for the ids an automatic change may have recorded raw. */
export interface TimelineNames {
  people: Map<number, string>;
  groups: Map<number, string>;
  statuses: Map<number, string>;
  priorities: Map<number, string>;
}

export function Timeline({
  entries,
  attachments,
  names,
  isAgent,
}: {
  entries: TimelineEntry[];
  attachments: StoredAttachment[];
  names?: TimelineNames;
  isAgent: boolean;
}) {
  if (entries.length === 0) {
    return <p className="py-8 text-center text-body text-ink-muted">Nothing has been said on this ticket yet.</p>;
  }

  // Files belong under the message that carried them; files attached to the
  // ticket itself belong with the original request.
  const filesFor = (message: TimelineMessage) =>
    attachments.filter(
      (file) =>
        (file.entity_type === 'conversation' && file.entity_id === message.id) ||
        (message.kind === 'description' && file.entity_type === 'ticket'),
    );

  // "Fiona Hart raised this ticket", straight under Fiona Hart's own request,
  // says nothing the request does not. It is kept when somebody else raised it
  // — an agent logging a phone call — because then it is the only place that
  // says so.
  const description = entries.find(
    (entry): entry is TimelineMessage => isTimelineMessage(entry) && entry.kind === 'description',
  );
  const shown = entries.filter(
    (entry) =>
      isTimelineMessage(entry) ||
      entry.activity_type !== 'created' ||
      !description ||
      entry.actor_id === null ||
      entry.actor_id !== description.author_id,
  );

  return (
    <ol className="space-y-3" aria-label="Conversation">
      {group(shown).map((item) =>
        item.kind === 'message' ? (
          <MessageEntry key={`m${item.entry.id}`} entry={item.entry} files={filesFor(item.entry)} isAgent={isAgent} />
        ) : (
          <EventRun key={`e${item.events[0]!.id}`} events={item.events} names={names} />
        ),
      )}
    </ol>
  );
}

type Grouped = { kind: 'message'; entry: TimelineMessage } | { kind: 'events'; events: TimelineEvent[] };

/** Consecutive events travel together, so a run of them can be folded. */
function group(entries: TimelineEntry[]): Grouped[] {
  const out: Grouped[] = [];
  for (const entry of entries) {
    if (isTimelineMessage(entry)) {
      out.push({ kind: 'message', entry });
      continue;
    }
    const last = out[out.length - 1];
    if (last?.kind === 'events') last.events.push(entry);
    else out.push({ kind: 'events', events: [entry] });
  }
  return out;
}

/**
 * Four or more updates in a row fold, so the messages stay close together —
 * all but the newest, which stays in sight. Folding the whole run hid the very
 * change an agent had just made: add a task, and the conversation's last line
 * read "5 updates" instead of saying so.
 */
const FOLD_AT = 4;

function EventRun({ events, names }: { events: TimelineEvent[]; names?: TimelineNames | undefined }) {
  const [open, setOpen] = useState(false);
  if (events.length < FOLD_AT || open) {
    return (
      <>
        {events.map((event) => (
          <EventEntry key={event.id} entry={event} names={names} />
        ))}
      </>
    );
  }
  const newest = events[events.length - 1]!;
  const folded = events.length - 1;
  return (
    <>
      <li>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex w-full items-center gap-2 rounded-control px-1 py-1 text-start text-body text-ink-muted transition-colors hover:bg-sunken hover:text-ink"
        >
          <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-sunken">
            <Clock aria-hidden className="size-3 text-ink-subtle" />
          </span>
          {folded} earlier updates — show them
        </button>
      </li>
      <EventEntry key={newest.id} entry={newest} names={names} />
    </>
  );
}

function MessageEntry({
  entry,
  files,
  isAgent,
}: {
  entry: TimelineMessage;
  files: StoredAttachment[];
  isAgent: boolean;
}) {
  const isNote = entry.kind === 'note';
  const author = entry.author_name ?? 'Service desk';

  return (
    <li>
      <Card
        className={cn(
          'min-w-0 overflow-hidden',
          // An internal note is unmistakably different: a warning-toned ground
          // and a padlock. Confusing one for a public reply is the expensive
          // mistake on this screen.
          isNote && 'border-caution-line bg-caution-soft',
        )}
      >
        <div
          className={cn(
            'flex flex-wrap items-center gap-x-2 gap-y-1 border-b px-3 py-2',
            isNote ? 'border-caution-line' : 'border-line bg-sunken/50',
          )}
        >
          <Avatar name={author} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-body font-medium text-ink">
              <bdi>{author}</bdi>
            </p>
            {isAgent && entry.author_email && (
              <p className="truncate text-mini text-ink-subtle">
                <bdi>{entry.author_email}</bdi>
              </p>
            )}
          </div>
          {isNote && (
            <Badge tone="caution">
              <Lock aria-hidden className="size-3" />
              Internal note
            </Badge>
          )}
          {entry.kind === 'description' && <Badge tone="neutral">Original request</Badge>}
          {entry.source === 'email' && (
            <span className="inline-flex items-center gap-1 text-mini text-ink-subtle">
              <Mail aria-hidden className="size-3" />
              by email
            </span>
          )}
          <time dateTime={entry.created_at} title={absoluteTime(entry.created_at)} className="shrink-0 text-mini text-ink-subtle">
            {relativeTime(entry.created_at)}
          </time>
        </div>

        <div className="px-3 py-3">
          <MessageBody text={entry.body_text} />
          <MessageAttachments files={files} />
        </div>
      </Card>
    </li>
  );
}

/**
 * A message body, wrapped wherever it has to be: a pasted SharePoint link or a
 * stack-trace line has no spaces to break at, and was clipped by the card.
 */
export function MessageBody({ text }: { text: string }) {
  if (!text.trim()) return <p className="text-body text-ink-subtle italic">No text — see the attachments.</p>;
  return (
    <p dir="auto" className="text-reading break-words whitespace-pre-wrap text-ink [overflow-wrap:anywhere]">
      {splitLinks(text).map((part, index) =>
        part.href ? (
          <a
            key={index}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-accent-ink underline decoration-accent-line underline-offset-2 hover:decoration-accent"
          >
            {part.text}
          </a>
        ) : (
          <Fragment key={index}>{part.text}</Fragment>
        ),
      )}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

const LINK_READINGS: Record<string, string> = {
  related: 'related to',
  duplicate_of: 'a duplicate of',
  duplicated_by: 'duplicated by',
  blocks: 'blocking',
  blocked_by: 'blocked by',
  caused_by: 'caused by',
  causes: 'the cause of',
};

const SLA_TARGETS: Record<string, string> = {
  first_response: 'first response',
  next_response: 'next response',
  resolution: 'resolution',
};

const TicketLink = ({ number }: { number: unknown }) =>
  typeof number === 'number' || (typeof number === 'string' && /^\d+$/.test(number)) ? (
    <Link to={`/tickets/${number}`} className="font-medium text-accent-ink hover:underline">
      #{String(number)}
    </Link>
  ) : (
    <>another ticket</>
  );

function quoted(value: unknown): ReactNode {
  return typeof value === 'string' && value ? (
    <>
      “<bdi>{value}</bdi>”
    </>
  ) : null;
}

/**
 * A changed value as words.
 *
 * Changes made through the ticket API are labelled when they are written. The
 * assignment engine and automation rules write raw ids, which read as
 * "assignee from nothing to 17" — so an id is looked up where the screen knows
 * the name, and otherwise the sentence simply leaves the value out.
 */
function valueName(field: string, value: unknown, names?: TimelineNames): string | null {
  if (value === null || value === undefined || value === '') return null;
  // Urgency and impact are now written in words; older entries hold the
  // stored 1–3, which read as "cleared urgency" for want of a name.
  if ((field === 'urgency' || field === 'impact') && typeof value === 'number') return levelLabel(value);
  if (typeof value === 'number') {
    const table =
      field === 'assignee_id'
        ? names?.people
        : field === 'group_id'
          ? names?.groups
          : field === 'status_id'
            ? names?.statuses
            : field === 'priority_id'
              ? names?.priorities
              : undefined;
    return table?.get(value) ?? null;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

const FIELD_NAMES: Record<string, string> = {
  status_id: 'status',
  priority_id: 'priority',
  assignee_id: 'assignee',
  group_id: 'group',
  department_id: 'department',
  category_id: 'category',
  type_id: 'type',
  subject: 'subject',
  urgency: 'urgency',
  impact: 'impact',
};

function fieldName(change: { field: string; label?: string | undefined }): string {
  return change.label ?? FIELD_NAMES[change.field] ?? change.field.replace(/^custom_fields\./, '').replace(/_id$/, '').replace(/_/g, ' ');
}

function describe(
  entry: TimelineEvent,
  names?: TimelineNames,
): {
  icon: typeof Zap;
  text: ReactNode;
  tone?: 'critical';
  /** A whole sentence, for an event nobody in particular did. */
  sentence?: ReactNode;
} {
  const meta = entry.metadata ?? {};
  const change = entry.changes[0];
  const to = change ? valueName(change.field, change.to, names) : null;
  const from = change ? valueName(change.field, change.from, names) : null;

  switch (entry.activity_type) {
    case 'created':
      return { icon: Sparkles, text: meta.source === 'email' ? 'raised this ticket by email' : 'raised this ticket' };
    case 'status_changed':
      if (meta.reopened) {
        return {
          icon: ArrowRightLeft,
          text: meta.via === 'email' ? 'reopened this ticket by replying to an email' : 'reopened this ticket by replying',
        };
      }
      // The requester answering a ticket that waited on them: their reply
      // moved it, they did not choose a status.
      if (meta.answered) {
        return {
          icon: ArrowRightLeft,
          text: (
            <>
              answered, so this ticket moved
              {from ? <> from <b className="font-medium text-ink">{from}</b></> : null}
              {to ? <> to <b className="font-medium text-ink">{to}</b></> : ' back to the service desk'}
            </>
          ),
        };
      }
      if (to) return { icon: ArrowRightLeft, text: <>changed the status{from ? <> from <b className="font-medium text-ink">{from}</b></> : null} to <b className="font-medium text-ink">{to}</b></> };
      return { icon: ArrowRightLeft, text: entry.actor_name === 'Auto-close' ? 'closed this ticket automatically' : 'changed the status' };
    case 'priority_changed': {
      // Nobody chose this priority: it followed the urgency or impact just set.
      const followed = meta.via === 'priority_matrix' ? ', following urgency and impact' : '';
      return {
        icon: Zap,
        text: to ? (
          <>
            changed the priority{from ? <> from {from}</> : null} to <b className="font-medium text-ink">{to}</b>
            {followed}
          </>
        ) : (
          `changed the priority${followed}`
        ),
      };
    }
    case 'assigned':
      if (change && (change.to === null || change.to === undefined)) return { icon: UserRound, text: 'unassigned this ticket' };
      return { icon: UserRound, text: to ? <>assigned this ticket to <b className="font-medium text-ink"><bdi>{to}</bdi></b></> : 'assigned this ticket' };
    case 'group_changed':
      if (change && (change.to === null || change.to === undefined)) return { icon: Users, text: 'took this ticket out of its group' };
      return { icon: Users, text: to ? <>moved this ticket to the <b className="font-medium text-ink"><bdi>{to}</bdi></b> group</> : 'moved this ticket to another group' };
    case 'subject_changed':
      return { icon: TagIcon, text: <>renamed this ticket {quoted(change?.to)}</> };
    case 'type_changed':
      return { icon: TagIcon, text: to ? <>changed the type to <b className="font-medium text-ink">{to}</b></> : 'changed the type' };
    case 'field_changed':
    case 'automation_applied': {
      const parts = entry.changes.map((one, index) => {
        const value = valueName(one.field, one.to, names);
        return (
          <Fragment key={index}>
            {index > 0 && ', '}
            {value === null ? (
              <>cleared {fieldName(one)}</>
            ) : (
              <>
                set {fieldName(one)} to <b className="font-medium text-ink"><bdi>{value}</bdi></b>
              </>
            )}
          </Fragment>
        );
      });
      return { icon: entry.activity_type === 'automation_applied' ? Zap : TagIcon, text: parts.length ? parts : 'updated this ticket' };
    }
    case 'task_added':
      return { icon: ListChecks, text: <>added the task {quoted(meta.title)}</> };
    case 'task_status_changed':
      return {
        icon: change?.to === 'completed' ? CheckCircle2 : ListChecks,
        text:
          change?.to === 'completed' ? (
            <>completed the task {quoted(meta.title)}</>
          ) : (
            <>
              marked the task {quoted(meta.title)} as {String(change?.to ?? 'changed').replace(/_/g, ' ')}
            </>
          ),
      };
    case 'task_removed':
      return { icon: ListChecks, text: <>removed the task {quoted(meta.title)}</> };
    case 'linked':
      return {
        icon: Link2,
        text: (
          <>
            marked this ticket as {LINK_READINGS[String(meta.link_type)] ?? 'related to'} <TicketLink number={meta.target_number} />
          </>
        ),
      };
    case 'parent_set':
      return { icon: Link2, text: <>made <TicketLink number={meta.parent_number} /> the parent of this ticket</> };
    case 'merged_in':
      return { icon: GitMerge, text: <>merged <TicketLink number={meta.source_number} /> into this ticket</> };
    case 'merged_into':
      return { icon: GitMerge, text: <>merged this ticket into <TicketLink number={meta.target_number} /></> };
    case 'unmerged':
      return meta.source_number !== undefined
        ? { icon: GitMerge, text: <>undid the merge of <TicketLink number={meta.source_number} />, whose messages went back to it</> }
        : { icon: GitMerge, text: <>undid the merge into <TicketLink number={meta.target_number} /></> };
    case 'sla_breached': {
      const target = SLA_TARGETS[String(meta.target)];
      return {
        icon: AlertTriangle,
        tone: 'critical',
        text: null,
        sentence: target ? `The ${target} target was missed.` : 'A service-level target was missed.',
      };
    }
    case 'unassigned_escalated':
      return {
        icon: AlertTriangle,
        tone: 'critical',
        text: null,
        sentence: entry.actor_name ? (
          <>
            Escalated: still unassigned in <bdi>{entry.actor_name}</bdi>.
          </>
        ) : (
          'Escalated: still unassigned.'
        ),
      };
    default:
      return { icon: Clock, text: entry.activity_type.replace(/_/g, ' ') };
  }
}

function EventEntry({ entry, names }: { entry: TimelineEvent; names?: TimelineNames | undefined }) {
  const { icon: Icon, text, tone, sentence } = describe(entry, names);
  const actor =
    entry.actor_name ??
    (entry.actor_type === 'automation' ? 'Automation' : entry.actor_type === 'system' ? 'The service desk' : 'Somebody');
  const reason = typeof entry.metadata?.reason === 'string' ? entry.metadata.reason : null;

  return (
    <li className="flex items-start gap-2 px-1 py-0.5">
      <span
        className={cn(
          'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full',
          tone === 'critical' ? 'bg-critical-soft text-critical-ink' : 'bg-sunken text-ink-subtle',
        )}
      >
        <Icon aria-hidden className="size-3" />
      </span>
      <p className={cn('min-w-0 flex-1 text-body break-words', tone === 'critical' ? 'text-critical-ink' : 'text-ink-muted')}>
        {sentence ?? (
          <>
            <span className="font-medium text-ink">
              <bdi>{actor}</bdi>
            </span>{' '}
            {text}
          </>
        )}
        {reason && (
          <span className="text-ink-subtle">
            {' '}
            — <bdi>{reason}</bdi>
          </span>
        )}
      </p>
      <time dateTime={entry.created_at} title={absoluteTime(entry.created_at)} className="mt-0.5 shrink-0 text-mini text-ink-subtle">
        {relativeTime(entry.created_at)}
      </time>
    </li>
  );
}
